import React, { useState, useEffect } from "react";
import { Card, Title, Button, Flex, Text } from "@tremor/react";
import SourceTable from "./table";
import SourceForm from "./form";
import { Dialog, DialogPanel } from "@tremor/react";
import Nav from "../../components/nav";

import * as sourcesAPI from "../../services/sources";
import { PlusCircleIcon, GlobeAltIcon } from "@heroicons/react/24/outline";

export default function Sources() {
  const [sources, setSources] = useState([]);
  const [page] = useState(1);
  const [limit] = useState(10);
  const [, setTotal] = useState(0);
  const [isOpen, setIsOpen] = React.useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [blockedProfiles, setBlockedProfiles] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const refresh = async () => {
    try {
      await getSources();
      setIsOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const onDelete = (id) => {
    const target = sources.find((s) => s._id === id);
    if (!target) return;
    setDeleteTarget(target);
    setBlockedProfiles(null);
    setDeleteError(null);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setBlockedProfiles(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await sourcesAPI.remove(deleteTarget._id);
      await getSources();
      setDeleteTarget(null);
      setBlockedProfiles(null);
    } catch (error) {
      if (error?.status === 400 && Array.isArray(error.profiles)) {
        setBlockedProfiles(error.profiles);
      } else {
        setDeleteError(error?.error || error?.message || "Failed to delete source");
      }
    } finally {
      setDeleting(false);
    }
  };

  const onUpdate = async (updatedSource) => {
    try {
      // Only send the specific fields we want to update
      const updateData = {
        name: updatedSource.name,
        description: updatedSource.description,
        enabled: updatedSource.enabled
      };
      
      console.log('Updating source with data:', updateData);
      await sourcesAPI.update(updatedSource._id, updateData);
      // Refresh the sources list to show updated data
      await getSources();
    } catch (error) {
      console.error(error);
      throw error; // Re-throw to show error in the UI
    }
  };

  const getSources = async () => {
    setLoading(true);
    try {
      const response = await sourcesAPI.list(page, limit);
      setSources(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Read operation
  useEffect(() => {
    getSources();
  }, []);

  // Add functions for Create, Update, and Delete operations here

  return (
    <div>
      <Nav />
      <main className="p-4 md:p-10 mx-auto max-w-7xl">
        <div className="mb-8 bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 p-6">
          <Flex justifyContent="between" alignItems="center">
            <div className="space-y-1">
              <Title className="text-2xl font-bold text-gray-900 dark:text-gray-50">
                Sources
              </Title>
              <Text className="text-gray-500 dark:text-gray-400 text-sm max-w-2xl">
                HTTP endpoints for sending data to Zafron.
              </Text>
            </div>
            <div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setIsOpen(true)}
                className="px-4 py-2 shadow-sm hover:shadow-md transition-all duration-200"
                icon={PlusCircleIcon}
              >
                New Source
              </Button>
            </div>
          </Flex>
        </div>

        <Card className="border border-gray-100 dark:border-gray-800 shadow-sm">
          <div className="px-1">
            {loading ? (
              <SourceTableSkeleton />
            ) : sources.length > 0 ? (
              <SourceTable sources={sources} onDelete={onDelete} onUpdate={onUpdate} />
            ) : (
              <NoData open={() => setIsOpen(true)} />
            )}
          </div>
        </Card>
      </main>

      <Dialog
        open={isOpen}
        onClose={(val) => setIsOpen(val)}
        static={true}
        className="relative z-50"
      >
        <DialogPanel className="max-w-2xl transform rounded-xl bg-white dark:bg-gray-900 p-6 shadow-xl transition-all">
          <SourceForm onCancel={refresh} />
        </DialogPanel>
      </Dialog>

      <Dialog
        open={deleteTarget !== null}
        onClose={closeDeleteDialog}
        static={true}
        className="relative z-50"
      >
        <DialogPanel className="max-w-md transform rounded-xl bg-white dark:bg-gray-900 p-6 shadow-xl transition-all">
          {blockedProfiles ? (
            <>
              <Title>Can't delete this source</Title>
              <Text className="mt-2">
                <strong>{deleteTarget?.name}</strong> is in use by{" "}
                {blockedProfiles.length} profile
                {blockedProfiles.length === 1 ? "" : "s"}. Detach or delete the
                profile{blockedProfiles.length === 1 ? "" : "s"} first, then try
                again.
              </Text>
              <ul className="mt-3 list-disc list-inside space-y-1 max-h-48 overflow-y-auto">
                {blockedProfiles.map((p) => (
                  <li key={p._id} className="text-sm text-gray-700 dark:text-gray-300">
                    {p.name || p._id}
                  </li>
                ))}
              </ul>
              <Flex justifyContent="end" className="mt-4">
                <Button onClick={closeDeleteDialog}>Close</Button>
              </Flex>
            </>
          ) : (
            <>
              <Title>Delete source?</Title>
              <Text className="mt-2">
                This will permanently delete{" "}
                <strong>{deleteTarget?.name}</strong>. This cannot be undone.
              </Text>
              {deleteError && (
                <div className="bg-red-100 dark:bg-red-950/40 px-2 py-1 mt-3">
                  <Text className="text-red-600 dark:text-red-300">{deleteError}</Text>
                </div>
              )}
              <Flex justifyContent="end" className="mt-4 space-x-2">
                <Button
                  variant="secondary"
                  onClick={closeDeleteDialog}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button color="red" onClick={confirmDelete} loading={deleting}>
                  Delete
                </Button>
              </Flex>
            </>
          )}
        </DialogPanel>
      </Dialog>
    </div>
  );
}

function SourceTableSkeleton() {
  return (
    <div className="animate-pulse">
      {/* Table Header */}
      <div className="flex items-center p-4 border-b border-gray-200 dark:border-gray-800">
        <div className="flex-1 grid grid-cols-6 gap-4">
          <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded"></div>
        </div>
      </div>
      
      {/* Table Rows */}
      {[...Array(5)].map((_, index) => (
        <div key={index} className="flex items-center p-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex-1 grid grid-cols-6 gap-4 items-center">
            {/* Source */}
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-3/4"></div>
              <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded w-1/2"></div>
            </div>
            
            {/* Provider */}
            <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-16"></div>
            
            {/* Status */}
            <div className="flex items-center space-x-2">
              <div className="h-4 w-4 bg-gray-200 dark:bg-gray-800 rounded"></div>
              <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-14"></div>
            </div>
            
            {/* Events */}
            <div className="space-y-1">
              <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-12"></div>
              <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded w-16"></div>
            </div>
            
            {/* Last Activity */}
            <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-16"></div>
            
            {/* Actions */}
            <div className="flex items-center space-x-2">
              <div className="h-8 w-8 bg-gray-200 dark:bg-gray-800 rounded"></div>
              <div className="h-8 w-8 bg-gray-200 dark:bg-gray-800 rounded"></div>
              <div className="h-8 w-8 bg-gray-200 dark:bg-gray-800 rounded"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function NoData({ open }) {
  return (
    <>
      <div className="mt-4 flex h-52 items-center justify-center rounded-tremor-small border border-dashed border-tremor-border p-4 dark:border-dark-tremor-border">
        <div className="text-center">
          <GlobeAltIcon
            className="mx-auto h-7 w-7 text-tremor-content-subtle dark:text-dark-tremor-content-subtle"
            aria-hidden={true}
          />

          <p className="mt-2 text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
            Empty
          </p>
          <p className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
            Create a new source to receive data from other platforms such as a
            LoRaWAN Network Server or generic HTTP endpoint.
          </p>
          <p></p>
          <Button
            icon={PlusCircleIcon}
            onClick={open}
            type="button"
            className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-tremor-small bg-tremor-brand px-3 py-2 text-tremor-default font-medium text-tremor-brand-inverted shadow-tremor-input hover:bg-tremor-brand-emphasis"
          >
            New Source
          </Button>
        </div>
      </div>
    </>
  );
}
